/**
 * Scenario Results Panel Component
 * Displays scenario simulation results and comparisons
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorDisplay } from '../common/ErrorDisplay';
import type {
  ScenarioSimulationResult,
  ScenarioModification,
} from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';

interface ScenarioResultsPanelProps {
  baselineData?: unknown[][];
  headers?: string[];
  onScenarioCreated?: (result: ScenarioSimulationResult) => void;
}

/**
 * Scenario Results Panel Component
 */
export const ScenarioResultsPanel: React.FC<ScenarioResultsPanelProps> = ({
  baselineData,
  headers,
  onScenarioCreated,
}) => {
  const [result, setResult] = useState<ScenarioSimulationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scenarioName, setScenarioName] = useState('Scenario 1');
  const [modifications, setModifications] = useState<ScenarioModification[]>([
    {
      columnIndex: 0,
      columnName: headers?.[0] || 'Column 1',
      operation: 'multiply',
      value: 1.1,
    },
  ]);

  /**
   * Run scenario simulation
   */
  const runSimulation = async () => {
    if (!baselineData || !headers) {
      setError('No data available for simulation');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/scenario-engine/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baselineData,
          headers,
          modifications,
          scenarioName,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const simulationResult: ScenarioSimulationResult = await response.json();
      setResult(simulationResult);

      if (onScenarioCreated) {
        onScenarioCreated(simulationResult);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add modification
   */
  const addModification = () => {
    setModifications([
      ...modifications,
      {
        columnIndex: 0,
        columnName: headers?.[0] || 'Column 1',
        operation: 'multiply',
        value: 1.1,
      },
    ]);
  };

  /**
   * Remove modification
   */
  const removeModification = (index: number) => {
    setModifications(modifications.filter((_, i) => i !== index));
  };

  /**
   * Update modification
   */
  const updateModification = (index: number, field: string, value: unknown) => {
    const updated = [...modifications];
    updated[index] = { ...updated[index], [field]: value };
    setModifications(updated);
  };

  /**
   * Get impact color
   */
  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'positive':
        return '#4CAF50';
      case 'negative':
        return '#F44336';
      case 'neutral':
        return '#FF9800';
      default:
        return '#999';
    }
  };

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>📈 Simulation de scénario</h3>
        <p className="preview-subtitle">Analyse what-if et comparaison de scénarios</p>
      </div>

      {error && <ErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {!baselineData && (
        <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', marginBottom: '12px', fontSize: '13px', color: '#e65100' }}>
          ⚠️ Aucune donnée disponible. Rafraîchissez la page pour charger les données Excel.
        </div>
      )}

      {!result ? (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {/* Nom du scénario */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
              Nom du scénario
            </label>
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
            />
          </div>

          {/* Modifications */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Modifications</label>
              <button
                onClick={addModification}
                style={{ padding: '4px 8px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
              >
                + Ajouter
              </button>
            </div>

            {modifications.map((mod, idx) => (
              <div key={idx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', border: '1px solid #ddd' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px' }}>
                  <select
                    value={mod.columnIndex}
                    onChange={(e) => updateModification(idx, 'columnIndex', parseInt(e.target.value))}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                  >
                    {headers?.map((h, i) => (
                      <option key={i} value={i}>{h}</option>
                    ))}
                  </select>

                  <select
                    value={mod.operation}
                    onChange={(e) => updateModification(idx, 'operation', e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                  >
                    <option value="multiply">Multiplier</option>
                    <option value="add">Ajouter</option>
                    <option value="set">Définir</option>
                    <option value="filter">Filtrer</option>
                  </select>

                  <input
                    type="text"
                    value={mod.value}
                    onChange={(e) => updateModification(idx, 'value', e.target.value)}
                    style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                  />

                  <button
                    onClick={() => removeModification(idx)}
                    style={{ padding: '6px 8px', backgroundColor: '#F44336', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={runSimulation}
            disabled={isLoading || !baselineData}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#2196F3', color: 'white',
              border: 'none', borderRadius: '4px',
              cursor: isLoading || !baselineData ? 'not-allowed' : 'pointer',
              opacity: isLoading || !baselineData ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Simulation en cours...' : 'Lancer la simulation'}
          </button>

          {isLoading && <LoadingSpinner label="Simulation du scénario..." />}
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 8px 0' }}>📈 Résultats</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Lignes modifiées</p>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0' }}>{result.delta.rowsChanged}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Cellules modifiées</p>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0' }}>{result.delta.affectedCells}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>% de changement</p>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0' }}>{result.delta.percentageChange.toFixed(1)}%</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Impact global</p>
                <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '0', color: getImpactColor(result.comparison.overallImpact) }}>
                  {result.comparison.overallImpact === 'positive' ? 'POSITIF' : result.comparison.overallImpact === 'negative' ? 'NÉGATIF' : 'NEUTRE'}
                </p>
              </div>
            </div>
          </div>

          {Object.keys(result.delta.metricChanges).length > 0 && (
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>📊 Changements de métriques</h4>
              {Object.entries(result.delta.metricChanges).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}>
                  <span>{key} :</span>
                  <span style={{ fontWeight: 'bold', color: value > 0 ? '#4CAF50' : '#F44336' }}>
                    {value > 0 ? '+' : ''}{typeof value === 'number' ? value.toFixed(2) : value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {result.comparison.improvements.length > 0 && (
            <div style={{ padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px', marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#2e7d32' }}>✅ Améliorations</h4>
              {result.comparison.improvements.map((imp, idx) => (
                <p key={idx} style={{ margin: '4px 0', fontSize: '13px', color: '#2e7d32' }}>• {imp}</p>
              ))}
            </div>
          )}

          {result.comparison.degradations.length > 0 && (
            <div style={{ padding: '12px', backgroundColor: '#ffebee', borderRadius: '4px', marginBottom: '12px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#c62828' }}>⚠️ Dégradations</h4>
              {result.comparison.degradations.map((deg, idx) => (
                <p key={idx} style={{ margin: '4px 0', fontSize: '13px', color: '#c62828' }}>• {deg}</p>
              ))}
            </div>
          )}

          <button
            onClick={() => setResult(null)}
            style={{ width: '100%', padding: '10px', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', marginTop: '12px' }}
          >
            ← Retour à la configuration
          </button>
        </div>
      )}
    </div>
  );
};
